import authRequest from '@dev-agents/fetch';

const CREDIT_API_URL = '/gateway/api/assist/api/rovo/v1/credits';

export type CreditRequestMethod = { mode: string; redirectUrl?: string };
export type CreditState = {
	denial: {
		scope: string;
		reason?: string;
		resetsAt?: number | null;
		requestMethod?: CreditRequestMethod | null;
		canRequestCredits?: boolean;
	} | null;
	warning: { scope: string; resetsAt: number | null } | null;
	asOf: number;
	refreshAfterSeconds: number;
};
export type CreditDetails = {
	denied: boolean;
	resetsAt: number | null;
	method: CreditRequestMethod | null;
};

export const creditRequest = (
	cloudId: string,
	path: 'state' | 'request-method' | 'requests',
	method: 'GET' | 'POST',
	signal: AbortSignal,
): Promise<Response> =>
	fetch(
		authRequest(`${CREDIT_API_URL}/${path}`, {
			method,
			credentials: 'include',
			signal,
			headers: {
				Accept: 'application/json',
				'X-Cloudid': cloudId,
				'X-Product': 'jira',
				'X-Experience-Id': 'ai-mate',
			},
		}),
	);

export const validRequestMethod = (method: CreditRequestMethod): CreditRequestMethod | null => {
	if (!method || typeof method.mode !== 'string') return null;
	if (method.mode !== 'REDIRECT_URL') return { mode: 'EMAIL' };
	if (typeof method.redirectUrl !== 'string' || method.redirectUrl.length > 2048) return null;
	try {
		const url = new URL(method.redirectUrl);
		return url.protocol === 'https:' && !url.username && !url.password ? method : null;
	} catch {
		return null;
	}
};

export const getCreditDetails = async (
	cloudId: string,
	signal: AbortSignal,
): Promise<CreditDetails | null> => {
	const response = await creditRequest(cloudId, 'state', 'GET', signal);
	if ([400, 401, 403, 404].includes(response.status)) return null;
	if (!response.ok) throw new Error(`Credit state lookup failed (${response.status})`);
	const { denial }: CreditState = await response.json();
	if (denial === undefined) throw new Error('Credit state response is missing denial');
	const limitReached = denial && (denial.reason === undefined || denial.reason === 'LIMIT_REACHED');
	const details: CreditDetails = {
		denied: denial !== null,
		resetsAt:
			limitReached &&
			typeof denial.resetsAt === 'number' &&
			Number.isFinite(new Date(denial.resetsAt).getTime())
				? denial.resetsAt
				: null,
		method: null,
	};
	if (denial?.scope !== 'USER' || !limitReached) return details;
	if (denial.requestMethod !== undefined)
		return {
			...details,
			method: denial.requestMethod ? validRequestMethod(denial.requestMethod) : null,
		};
	if (denial.canRequestCredits !== true) return details;
	// Compatibility with state responses that only expose the eligibility flag.
	const methodResponse = await creditRequest(cloudId, 'request-method', 'GET', signal);
	return {
		...details,
		method: methodResponse.ok ? validRequestMethod(await methodResponse.json()) : null,
	};
};
